/**
 * Step 6 - Progresso & Resultado.
 *
 * Renderiza:
 *   - Barra de progresso baseada no agregado de batches
 *   - Contadores de sucessos/falhas/pulados + em fila/processando/atrasados
 *   - Aviso quando há lote atrasado (recuperação automática pelo watchdog)
 *   - Aba "Erros" com itens failed/skipped e botão de reprocesso individual
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v1.7.0 - Separa stat "Falhas" em dois: "Inválidos"
 *            (invalid + duplicate + blacklisted) e "Falhas" (failed_items).
 *            Grid aumentado para md:grid-cols-4.
 *   RELEASE: 2026-05-17 v1.6.0 - Adiciona stat "Itens na fila" (valid_count -
 *            succeeded - failed - skipped) e corrige stat "Falhas" para somar
 *            invalid_count + duplicate_count + blacklisted_count + failed_items
 *            (consistência com a listagem CrmDealsImport v1.3.0).
 *   RELEASE: 2026-05-17 v1.5.0 - Botão "Cancelar importação" no cabeçalho do
 *            Step 6, visível apenas quando o job está em
 *            enqueued/processing/validating/awaiting_approval. O AlertDialog
 *            de confirmação é responsabilidade do componente pai
 *            (ImportDealsWizard) via prop `onCancel`. Reusa a RPC
 *            cancel_deal_import_job já existente.
 *   RELEASE: 2026-05-16 v1.4.0 - Mostra contadores adicionais (em fila,
 *            processando, atrasados) e banner explicativo quando há lote
 *            atrasado / aguardando retry, em vez de aparentar travamento.
 *            Consome novos campos batches_summary.stalled, last_batch_update
 *            e last_batch_error retornados por get_deal_import_job_details
 *            v1.1.0.
 *   RELEASE: 2026-05-16 v1.3.0 - Layout robusto.
 *   RELEASE: 2026-05-16 v1.2.0 - Estados de loading/erro/sem dados.
 *   RELEASE: 2026-05-16 v1.1.0 - Aba de erros com reprocesso por item.
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial.
 */

import { Loader2, CheckCircle2, RotateCw, AlertTriangle, Clock, XCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useDealImport } from '@/hooks/useDealImport';
import { Stat } from './Stat';

// Status do job em que o usuário ainda pode cancelar (lotes restantes serão
// pulados pelo worker process_deal_import_batch).
const CANCELLABLE_STATUSES = new Set([
  'validating',
  'validated',
  'awaiting_approval',
  'enqueued',
  'processing',
]);

interface Step6ProgressProps {
  isLoading: boolean;
  details: any | null | undefined;
  jobId: string | null;
  error?: unknown;
  /** Callback do pai para abrir o AlertDialog de confirmação de cancelamento. */
  onCancel?: () => void;
  /** Mostra spinner no botão enquanto a mutation de cancelar está em curso. */
  cancelPending?: boolean;
}

export function Step6Progress({ isLoading, details, jobId, error, onCancel, cancelPending }: Step6ProgressProps) {
  const { toast } = useToast();
  const api = useDealImport();
  const errorsQuery = api.useJobItems(
    jobId,
    ['failed', 'skipped', 'invalid'],
    { limit: 500, refetchInterval: 10000 },
  );

  // Estado: ainda carregando primeiro fetch
  if (isLoading && !details) {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">Carregando detalhes do job…</p>
      </div>
    );
  }

  // Estado: erro no polling
  if (error) {
    const msg = (error as any)?.message ?? String(error);
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
        <div className="text-sm min-w-0">
          <p className="font-medium text-destructive">Falha ao carregar progresso</p>
          <p className="text-muted-foreground mt-1 break-all">{msg}</p>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        Aguardando dados do job…
      </div>
    );
  }

  const job = details?.job ?? {};
  const summary = details?.batches_summary ?? {};
  const total = summary.total ?? 0;
  const done = (summary.completed ?? 0) + (summary.failed ?? 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isComplete = job.status === 'completed' || job.status === 'completed_with_errors';
  const errors = errorsQuery.data ?? [];

  // v1.4.0 - sinais de saúde do processamento
  const stalled = Number(summary.stalled ?? 0);
  const enqueued = Number(summary.enqueued ?? 0);
  const processing = Number(summary.processing ?? 0);
  const lastBatchUpdate = details?.last_batch_update ?? null;
  const lastBatchError = details?.last_batch_error ?? null;

  // Quanto tempo sem evolução nos lotes? (usado para banner informativo)
  const lastUpdateAgeSec = lastBatchUpdate
    ? Math.max(0, Math.floor((Date.now() - new Date(lastBatchUpdate).getTime()) / 1000))
    : null;
  const noProgressForAWhile =
    !isComplete && (enqueued > 0 || processing > 0) &&
    lastUpdateAgeSec !== null && lastUpdateAgeSec > 30;

  async function handleRequeue(itemId: string) {
    try {
      await api.requeueItem.mutateAsync(itemId);
      toast({ title: 'Item reenfileirado', description: 'Será reprocessado em até 5s.' });
    } catch (e: any) {
      toast({ title: 'Falha ao reprocessar', description: e.message, variant: 'destructive' });
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 gap-4">
      {/* Cabeçalho fixo */}
      <div className="shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {isComplete
              ? <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
              : <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />}
            <span className="font-medium capitalize truncate">{job.status?.replace(/_/g, ' ')}</span>
          </div>

          {/* v1.5.0 - Botão de cancelar (delegado ao pai via onCancel) */}
          {onCancel && CANCELLABLE_STATUSES.has(job.status) && (
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive flex-shrink-0"
              onClick={onCancel}
              disabled={cancelPending}
              title="Cancelar esta importação"
            >
              {cancelPending
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <XCircle className="h-4 w-4 mr-1.5" />}
              Cancelar importação
            </Button>
          )}
        </div>

        <Progress value={pct} />
        <p className="text-sm text-muted-foreground">
          {done} de {total} lotes processados ({pct}%)
        </p>

        {/* v1.4.0 - Banner de saúde do processamento */}
        {stalled > 0 && (
          <div className="rounded-lg border border-amber-300/40 bg-amber-50 dark:bg-amber-950/20 p-3 flex gap-3">
            <Clock className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                Recuperando {stalled} lote{stalled > 1 ? 's' : ''} atrasado{stalled > 1 ? 's' : ''}
              </p>
              <p className="text-amber-800/80 dark:text-amber-200/70 mt-0.5">
                O sistema detectou lentidão e está reagendando automaticamente.
                Nenhuma ação é necessária.
              </p>
            </div>
          </div>
        )}

        {stalled === 0 && noProgressForAWhile && (
          <div className="rounded-lg border border-muted bg-muted/30 p-3 flex gap-3">
            <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground">
              Aguardando próximo ciclo do worker (~5s). Sem evolução há {lastUpdateAgeSec}s.
            </div>
          </div>
        )}

        {lastBatchError && (lastBatchError.includes('error') || lastBatchError.includes('falha')) && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 flex gap-3">
            <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-xs min-w-0">
              <p className="font-medium text-destructive">Último erro de lote</p>
              <p className="text-muted-foreground mt-0.5 break-all">{lastBatchError}</p>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="summary" className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        <TabsList className="shrink-0 self-start">
          <TabsTrigger value="summary">Resumo</TabsTrigger>
          <TabsTrigger value="errors">Erros ({errors.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="flex-1 min-h-0 min-w-0 overflow-y-auto mt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm rounded-lg border p-4">
            <Stat label="Sucessos" value={job.succeeded_items ?? 0} variant="success" />
            <Stat
              label="Inválidos"
              value={
                (job.invalid_count ?? 0) +
                (job.duplicate_count ?? 0) +
                (job.blacklisted_count ?? 0)
              }
              variant="warning"
            />
            <Stat
              label="Falhas"
              value={job.failed_items ?? 0}
              variant="destructive"
            />
            <Stat label="Pulados" value={job.skipped_items ?? 0} variant="warning" />
            <Stat
              label="Itens na fila"
              value={Math.max(
                0,
                (job.valid_count ?? 0) -
                  (job.succeeded_items ?? 0) -
                  (job.failed_items ?? 0) -
                  (job.skipped_items ?? 0),
              )}
            />
            <Stat label="Lotes em fila" value={enqueued} />
            <Stat label="Lotes processando" value={processing} />
            <Stat
              label="Lotes atrasados"
              value={stalled}
              variant={stalled > 0 ? 'warning' : undefined as any}
            />
          </div>
        </TabsContent>

        {/* Erros: wrapper contido + tabela com scroll vertical/horizontal próprio */}
        <TabsContent value="errors" className="flex-1 min-h-0 min-w-0 mt-3 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 min-w-0 rounded-lg border overflow-auto">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Linha</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="min-w-[280px]">Motivo</TableHead>
                  <TableHead className="text-right w-32">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {errorsQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                )}
                {!errorsQuery.isLoading && errors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      Sem erros para exibir.
                    </TableCell>
                  </TableRow>
                )}
                {errors.map((it) => {
                  const reason =
                    it.validation_errors
                      ? typeof it.validation_errors === 'string'
                        ? it.validation_errors
                        : JSON.stringify(it.validation_errors)
                      : it.last_error ?? '—';
                  return (
                    <TableRow key={it.id}>
                      <TableCell className="font-mono text-xs">{it.row_index}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{it.status}</Badge>
                      </TableCell>
                      {/* div interno limita largura sem depender do layout intrínseco do <table> */}
                      <TableCell className="text-xs">
                        <div className="max-w-[420px] truncate" title={reason}>
                          {reason}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => handleRequeue(it.id)}
                          disabled={api.requeueItem.isPending}
                          title="Reprocessar este item"
                        >
                          <RotateCw className="h-3 w-3 mr-1" />
                          Reprocessar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
